import React, { useState, useRef, useEffect, useImperativeHandle } from "react";
import { Modal, Button } from "react-bootstrap";

const SignatureModel = ({ show, setShow, setSignatureUrl, customerData }) => {
    // Local state
    const [activeTab, setActiveTab] = useState("draw"); // draw or upload
    const [uploadedSignature, setUploadedSignature] = useState(null);
    const [penColor, setPenColor] = useState("black");
    
    const signaturePadRef = useRef();
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);

    // Expose signature pad methods to parent components via signaturePadRef
    useImperativeHandle(signaturePadRef, () => ({
        getCanvas: () => canvasRef.current,
        clear: () => {
            const canvas = canvasRef.current;
            if (canvas) {
                const ctx = canvas.getContext("2d");
                ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
        }
    }));

    // Initialize canvas dimensions once when shown or tab changed
    useEffect(() => {
        if (!show || activeTab !== "draw") return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const width = 700;
        const height = 200;

        canvas.width = width * ratio;
        canvas.height = height * ratio;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.lineWidth = 2.5 * ratio;
        ctx.strokeStyle = penColor;
    }, [show, activeTab]);

    // Update stroke style when penColor changes
    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext("2d");
            ctx.strokeStyle = penColor;
        }
    }, [penColor]);

    const getCoordinates = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();

        let clientX, clientY;
        if (e.touches && e.touches.length > 0) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            clientX = e.clientX;
            clientY = e.clientY;
        }

        // Universal projection: Map visual screen pixels to backing store pixels
        return {
            x: (clientX - rect.left) * (canvas.width / rect.width),
            y: (clientY - rect.top) * (canvas.height / rect.height)
        };
    };

    const startDrawing = (e) => {
        const coords = getCoordinates(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.strokeStyle = penColor;
        ctx.beginPath();
        ctx.moveTo(coords.x, coords.y);
        setIsDrawing(true);
        if (e.cancelable) e.preventDefault();
    };

    const draw = (e) => {
        if (!isDrawing) return;
        const coords = getCoordinates(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.lineTo(coords.x, coords.y);
        ctx.stroke();
        if (e.cancelable) e.preventDefault();
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file && file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = (e) => setUploadedSignature(e.target.result);
            reader.readAsDataURL(file);
        } else {
            alert("Please upload a valid image file.");
        }
    };

    const handleSaveSignature = () => {
        let signatureDataUrl;

        if (activeTab === "draw" && canvasRef.current) {
            signatureDataUrl = canvasRef.current.toDataURL("image/png");
        } else if (activeTab === "upload" && uploadedSignature) {
            signatureDataUrl = uploadedSignature;
        }

        if (signatureDataUrl) {
            setSignatureUrl(signatureDataUrl); // send data back to parent
            setShow(false);
        } else {
            alert("Please add a signature before saving.");
        }
    };
    const handleClearSignature = () => {
        if (activeTab === "draw" && signaturePadRef.current) {
            signaturePadRef.current.clear(); // clear canvas
        } else if (activeTab === "upload") {
            setUploadedSignature(null); // remove uploaded image
        }
    };


    return (
        <Modal show={show} onHide={() => setShow(false)} centered size="lg" className="signature-modal">
            <Modal.Header closeButton>
                <Modal.Title>Add Signature</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Tabs: Draw / Upload */}
                <div className="d-flex border-bottom mb-3">
                    <Button
                        variant={activeTab === "draw" ? "link text-primary" : "link text-secondary"}
                        onClick={() => setActiveTab("draw")}
                        className="me-3"
                    >
                        Draw
                    </Button>
                    <Button
                        variant={activeTab === "upload" ? "link text-primary" : "link text-secondary"}
                        onClick={() => setActiveTab("upload")}
                        className="me-3"
                    >
                        Upload
                    </Button>
                </div>

                {/* Draw Mode */}
                {activeTab === "draw" && (
                    <div className="signature-draw-wrapper text-center">
                        {/* Pen color selection */}
                        <div className="mb-2 d-flex justify-content-end gap-2">
                            {["black", "blue", "purple"].map((color) => (
                                <div
                                    key={color}
                                    onClick={() => setPenColor(color)}
                                    style={{
                                        width: 18,
                                        height: 18,
                                        borderRadius: "50%",
                                        backgroundColor: color,
                                        border: penColor === color ? "2px solid #000" : "1px solid #ccc",
                                        cursor: "pointer",
                                    }}
                                />
                            ))}
                        </div>

                        <div style={{ width: "100%", overflowX: "auto", padding: "10px 0" }}>
                            <canvas
                                ref={canvasRef}
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                onTouchStart={startDrawing}
                                onTouchMove={draw}
                                onTouchEnd={stopDrawing}
                                style={{
                                    border: "1px dashed #ccc",
                                    borderRadius: "6px",
                                    display: "block",
                                    margin: "0 auto",
                                    cursor: "crosshair",
                                    backgroundColor: "#fff"
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Upload Mode */}
                {activeTab === "upload" && (
                    <div className="text-center">
                        <input type="file" accept="image/*" onChange={handleFileUpload} className="form-control mb-3" />
                        {uploadedSignature && (
                            <img
                                src={uploadedSignature}
                                alt="Uploaded Signature"
                                className="img-fluid border rounded"
                                style={{ maxHeight: "200px" }}
                            />
                        )}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <div className="d-flex justify-content-between w-100">
                    <Button variant="secondary" onClick={handleClearSignature}>
                        Clear
                    </Button>
                    <Button variant="primary" onClick={handleSaveSignature}>
                        Done
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default SignatureModel;
